/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react';

import { views } from '../constants/event';
import calcFormula from '../utils/chart/ChartFormula';
import { DEFAULT_GROUP } from '../constants/common';
import { grandTotalSorting } from '../utils/chart/ChartManagementFeatures/sortChartFeatures';
import toLineConfig from './LineChart';
import { toColumnConfig } from './ColumnChart';
import toSunburstConfig from './SunburstChart';
import toChartData from './ChartDataConverter';
import ChartHolder from './ChartHolder';
import toEmptyConfig from './EmptyConfig';
import { isDataEmpty } from './Common';
import toPercentageAreaConfig from './PercentageAreaChart';
import toStackedAreaConfig from './StackedAreaChart';

const getConfig = (view) => {
  switch (view) {
    case views.CHART:
      return toLineConfig;
    case views.PERCENTAGE_CHART:
      return toPercentageAreaConfig;
    case views.STACKED_CHART:
      return toStackedAreaConfig;
    case views.COLUMN_CHART:
      return toColumnConfig;
    case views.PIE_CHART:
      return toSunburstConfig;
    default:
      return toEmptyConfig;
  }
};

export const changeEventNames = (data, revenueType) =>
  revenueType ? data.map((d) => ({ ...d, name: revenueType })) : data;

const EventChart = ({
  result,
  executeTime,
  options,
  revenueType,
  timezone,
  group = DEFAULT_GROUP,
  setChart,
  height,
  fromDashboard,
  triggerLabels,
  unit,
  showSort,
}) => {
  const { enteredFormula, isFormulaOnly } = options;

  const groupBy = result[0]?.info?.groupBy;

  const data = useMemo(() => changeEventNames(toChartData(result), revenueType), [executeTime]);

  const showCalcTotal = (chart) => grandTotalSorting(chart, options.columnChartSorting, group);

  const toConfig = useCallback(
    (dataParam, single) => {
      const toChartConfig = isDataEmpty(dataParam) ? toEmptyConfig : getConfig(options.view);
      const chartConfigOptions = { ...options, timezone, group, groupBy };

      const dataWithCumulative = dataParam.map((dataItem) => {
        const currentResult = result.find(({ info }) => info.uniqueChar === dataItem.id);

        return {
          ...dataItem,
          showCumulative:
            !revenueType &&
            !!currentResult?.info.query.aggregations.find(({ type }) => type === 'TOTAL'),
        };
      });

      const chartConfig = toChartConfig(
        dataWithCumulative,
        chartConfigOptions,
        showCalcTotal,
        fromDashboard,
        revenueType,
        showSort,
        single,
        triggerLabels,
        unit?.id
      );

      return chartConfig;
    },
    [options.view, executeTime, options.singleChartMode, enteredFormula, options.columnChartSorting]
  );

  // для подсчета данных с формулой
  const dataWithFormula = useMemo(
    () =>
      enteredFormula && options.view !== views.PIE_CHART
        ? calcFormula(data, enteredFormula, isFormulaOnly)
        : data,
    [enteredFormula, executeTime, options.view]
  );

  return (
    <ChartHolder
      setChart={setChart}
      data={dataWithFormula}
      toConfig={toConfig}
      single={options.singleChartMode}
      height={height}
      unitId={unit?.id}
    />
  );
};

export default EventChart;
